import { useState, useRef, useEffect } from "react";
import { BsCalendar2Date } from "react-icons/bs";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DatePicker = ({ value, onChange, placeholder = "SELECT DATE" }) => {
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const ref = useRef(null);

  const selectedDate = value ? new Date(value) : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDateSelect = (day) => {
    const month = String(viewMonth + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const formatted = `${viewYear}-${month}-${dayStr}`;
    onChange({ target: { value: formatted } });
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const isToday = (day) =>
    day === today.getDate() &&
    viewMonth === today.getMonth() &&
    viewYear === today.getFullYear();

  const isSelected = (day) =>
    selectedDate &&
    day === selectedDate.getDate() &&
    viewMonth === selectedDate.getMonth() &&
    viewYear === selectedDate.getFullYear();

  const totalDays = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);

  const yearList = Array.from(
    { length: 20 },
    (_, i) => today.getFullYear() - 10 + i,
  );

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString("en-GB").replace(/\//g, "-")
    : null;

  return (
    <div className="relative w-full" ref={ref}>
      {/* Input Trigger */}
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-3 px-4 py-[0.6rem] rounded-lg border bg-gray-50 cursor-pointer transition-all duration-300
          ${isOpen ? "border-gray-800 bg-white shadow-[0_0_0_3px_rgba(17,24,39,0.08)]" : "border-gray-200 hover:border-gray-500 hover:bg-white hover:shadow-md"}`}
      >
        <BsCalendar2Date
          className={`text-lg transition-colors duration-300 ${isOpen ? "text-gray-800" : "text-gray-400"}`}
        />
        <span
          className={`text-sm font-medium tracking-wide uppercase ${displayValue ? "text-gray-800" : "text-gray-400"}`}
        >
          {displayValue || placeholder}
        </span>

        {/* Animated bottom border */}
        {/* <div
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-gray-800 rounded-full transition-all duration-300 ${isOpen ? "w-full" : "w-0"}`}
        /> */}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-[110%] left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              className="p-1 rounded-md hover:bg-gray-100 transition"
            >
              <IoIosArrowBack className="text-gray-600 text-lg" />
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowYearPicker((prev) => !prev)}
                className="text-sm font-semibold text-gray-800 hover:text-black tracking-wide transition"
              >
                {MONTHS[viewMonth]} {viewYear}
              </button>
            </div>

            <button
              onClick={handleNextMonth}
              className="p-1 rounded-md hover:bg-gray-100 transition"
            >
              <IoIosArrowForward className="text-gray-600 text-lg" />
            </button>
          </div>

          {/* Year Picker */}
          {showYearPicker && (
            <div className="grid grid-cols-4 gap-1 mb-4 max-h-32 overflow-y-auto scrollbar-hide">
              {yearList.map((year) => (
                <button
                  key={year}
                  onClick={() => {
                    setViewYear(year);
                    setShowYearPicker(false);
                  }}
                  className={`text-xs py-1 rounded-md font-medium transition
                    ${year === viewYear ? "bg-gray-800 text-white" : "hover:bg-gray-100 text-gray-600"}`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Day Labels */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map((d) => (
              <div
                key={d}
                className="text-center text-[10px] font-semibold text-gray-400 uppercase"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-y-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: totalDays }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  onClick={() => handleDateSelect(day)}
                  className={`text-xs py-[6px] rounded-md font-medium transition-all duration-150
                    ${isSelected(day) ? "bg-gray-800 text-white shadow-md" : ""}
                    ${isToday(day) && !isSelected(day) ? "border border-gray-800 text-gray-800" : ""}
                    ${!isSelected(day) && !isToday(day) ? "hover:bg-gray-100 text-gray-700" : ""}`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Today Button */}
          <div className="mt-4 border-t border-gray-100 pt-3 flex justify-between items-center">
            <button
              onClick={() => {
                setViewMonth(today.getMonth());
                setViewYear(today.getFullYear());
                handleDateSelect(today.getDate());
              }}
              className="text-xs font-semibold text-gray-500 hover:text-gray-800 transition"
            >
              Today
            </button>
            {selectedDate && (
              <button
                onClick={() => {
                  onChange({ target: { value: "" } });
                  setIsOpen(false);
                }}
                className="text-xs font-semibold text-red-400 hover:text-red-600 transition"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
